// AgentViewModal.tsx — read-only view of one agent record, with the shortcuts
// that matter: edit, see their leads, move back to Leads.

import React from 'react';
import { ArrowLeftRight, Briefcase, Eye, ListChecks, MapPin, MessageCircle, Pencil, Phone, X } from 'lucide-react';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { G, C } from '../../../components/GlobalComponents/uiTokens';
import ProposalDialog from '../../Proposals/parts/ProposalDialog';
import { fmtDate } from '../../Proposals/parts/proposalTypes';
import { Agent, fmtPhone, waLink } from './agentTypes';

interface AgentViewModalProps {
  visible: boolean;
  agent: Agent | null;
  /** admin-only actions are hidden for anyone else (the page is admin-only anyway) */
  onHide: () => void;
  onEdit: (a: Agent) => void;
  onViewLeads: (a: Agent) => void;
  onMoveToLeads: (a: Agent) => void;
}

const LABEL: React.CSSProperties = { fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: C.muted, marginBottom: '3px' };
const VALUE: React.CSSProperties = { fontSize: '0.8rem', color: '#1f2937', lineHeight: 1.5, wordBreak: 'break-word' };

const Field: React.FC<{ label: string; children?: React.ReactNode }> = ({ label, children }) => (
  <div style={{ minWidth: 0 }}>
    <div style={LABEL}>{label}</div>
    <div style={VALUE}>{children || <span style={{ color: C.muted }}>—</span>}</div>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ border: `1px solid ${G.line}`, borderRadius: '9px', padding: '11px 13px', background: C.white }}>
    <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: C.teal, marginBottom: '9px' }}>{title}</div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '11px 14px' }}>{children}</div>
  </div>
);

const AgentViewModal: React.FC<AgentViewModalProps> = ({ visible, agent: a, onHide, onEdit, onViewLeads, onMoveToLeads }) => {
  if (!a) return null;

  return (
    <ProposalDialog
      visible={visible}
      onHide={onHide}
      title={a.name}
      subtitle={[a.profession, a.company].filter(Boolean).join(' · ') || 'Agent'}
      icon={Eye}
      maxWidth="560px"
      footer={
        <>
          <GlobalButton label="MOVE TO LEADS" variant="outline" icon={ArrowLeftRight} onClick={() => onMoveToLeads(a)} />
          <GlobalButton label="EDIT" variant="secondary" icon={Pencil} onClick={() => onEdit(a)} />
          <GlobalButton label="CLOSE" icon={X} onClick={onHide} />
        </>
      }
    >
      {/* status + leads strip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span
          style={{
            padding: '3px 10px',
            borderRadius: '20px',
            fontSize: '0.64rem',
            fontWeight: 700,
            background: a.is_active ? 'rgba(34,197,94,0.12)' : '#f3f4f6',
            color: a.is_active ? '#15803d' : C.sub,
            border: `1px solid ${a.is_active ? 'rgba(34,197,94,0.35)' : G.line}`,
          }}
        >
          {a.is_active ? 'Active' : 'Inactive'}
        </span>
        <span style={{ fontSize: '0.72rem', color: C.sub }}>
          <strong style={{ color: C.teal }}>{a.lead_count}</strong> lead{a.lead_count === 1 ? '' : 's'} brought in · <strong style={{ color: C.teal }}>{a.converted_count}</strong> converted
        </span>
        {a.lead_count > 0 && (
          <button
            type="button"
            onClick={() => onViewLeads(a)}
            style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'transparent', border: 'none', color: C.teal, fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
          >
            <ListChecks size={13} /> View their leads
          </button>
        )}
      </div>

      <Section title="Contact">
        <Field label="Phone">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '9px' }}>
            <a href={`tel:${a.phone}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', color: '#1f2937', textDecoration: 'none', fontWeight: 600 }}>
              <Phone size={12} color={C.tealMid} /> {fmtPhone(a.phone)}
            </a>
            <a href={waLink(a.phone)} target="_blank" rel="noopener noreferrer" title="Open WhatsApp" style={{ display: 'inline-flex', color: '#128c4a' }}>
              <MessageCircle size={15} />
            </a>
          </span>
        </Field>
        <Field label="Email">{a.email && <a href={`mailto:${a.email}`} style={{ color: '#1f2937' }}>{a.email}</a>}</Field>
      </Section>

      <Section title="Work">
        <Field label="Profession">{a.profession}</Field>
        <Field label="Company">{a.company}</Field>
        <Field label="Commission">{a.commission != null ? `${a.commission}%` : null}</Field>
      </Section>

      <Section title="Location">
        <Field label="City">
          {a.city && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <MapPin size={12} color={C.tealMid} /> {a.city}
            </span>
          )}
        </Field>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Address">{a.address}</Field>
        </div>
      </Section>

      {a.notes && (
        <Section title="Notes">
          <div style={{ gridColumn: '1 / -1', ...VALUE, whiteSpace: 'pre-wrap' }}>{a.notes}</div>
        </Section>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', fontSize: '0.68rem', color: C.muted }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <Briefcase size={11} /> Added {fmtDate(a.created_at)}
        </span>
        {a.from_lead && <span>Moved from Leads on {fmtDate(a.from_lead.transferred_at)}{a.from_lead.source ? ` · source ${a.from_lead.source.replace('_', ' ')}` : ''}</span>}
        {a.updated_at !== a.created_at && <span>Updated {fmtDate(a.updated_at)}</span>}
      </div>
    </ProposalDialog>
  );
};

export default AgentViewModal;